import { useState } from "react";
import { ArrowRight, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";
import { Button } from "../ui/button";
import { toast } from "sonner";
import { partnerService } from "../../services";

export default function PartnerModal({ open, onOpenChange }) {
  const [form, setForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: "",
    interest: "counsellor",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 5) {
      toast.error("Please share a bit more about your interest (min 5 chars).");
      return;
    }
    setSubmitting(true);
    try {
      await partnerService.submitLead(form);
      toast.success("Thank you! Our partnerships team will reach out soon.");
      onOpenChange(false);
      setForm({
        name: "",
        email: "",
        organization: "",
        role: "",
        interest: "counsellor",
        message: "",
      });
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not submit. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg rounded-3xl border-[#C89B3C]/30"
        data-testid="partner-modal"
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-semibold text-[#063B30]">
            Partner with Crescet
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Tell us a little about you. Our partnerships team will get back within 2 business days.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="grid gap-4 pt-2">
          <div className="grid gap-2">
            <Label htmlFor="p-name">Full name</Label>
            <Input
              id="p-name"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              placeholder="Your name"
              data-testid="partner-name-input"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="p-email">Email</Label>
            <Input
              id="p-email"
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              placeholder="you@organisation.com"
              data-testid="partner-email-input"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-2">
              <Label htmlFor="p-org">Organisation</Label>
              <Input
                id="p-org"
                value={form.organization}
                onChange={(e) => update("organization", e.target.value)}
                placeholder="School / Institute"
                data-testid="partner-org-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="p-role">Your role</Label>
              <Input
                id="p-role"
                value={form.role}
                onChange={(e) => update("role", e.target.value)}
                placeholder="Founder, Counsellor..."
                data-testid="partner-role-input"
              />
            </div>
          </div>
          <div className="grid gap-2">
            <Label>I am interested as a</Label>
            <Select value={form.interest} onValueChange={(v) => update("interest", v)}>
              <SelectTrigger data-testid="partner-interest-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="counsellor">Career Counsellor</SelectItem>
                <SelectItem value="tutor">Tutor / Trainer</SelectItem>
                <SelectItem value="institution">School / College / University</SelectItem>
                <SelectItem value="assessment">Assessment Provider</SelectItem>
                <SelectItem value="training">Training Company</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="p-msg">How can we collaborate?</Label>
            <Textarea
              id="p-msg"
              required
              rows={4}
              value={form.message}
              onChange={(e) => update("message", e.target.value)}
              placeholder="Briefly describe your offering and how you'd like to partner..."
              data-testid="partner-message-input"
            />
          </div>
          <DialogFooter className="mt-2">
            <Button
              type="submit"
              disabled={submitting}
              className="rounded-full bg-[#0B5D4B] px-6 py-5 text-white hover:bg-[#063B30]"
              data-testid="partner-submit-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 animate-spin" size={16} /> Sending...
                </>
              ) : (
                <>
                  Submit Enquiry <ArrowRight className="ml-2" size={16} />
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
